import {
	Avatar,
	Box,
	Button,
	Flex,
	Input,
	InputGroup,
	InputLeftElement,
	Select,
	Stack,
	Switch,
	Table,
	TableCaption,
	Tbody,
	Td,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast
} from '@chakra-ui/react';
import DashLayout from '../../components/DashLayout';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Loading } from '../../components/Loading';
import { FaPlus, FaSearch, FaUserGraduate } from 'react-icons/fa';
import { BiBookBookmark } from 'react-icons/bi';

import { useCallback, useEffect, useState } from 'react';
import { disableUser, listarAlunosPorProfessorId, listarProfessores } from '../../services/api';
import { ProfessoresGet } from '../Professores';
import { DrawerCreate } from './DrawerCreate';
import { DrawerUpdate } from './DrawerUpdate';
import useAuth from '../../hooks/useAuth';
import { LiberarMaterial } from './LiberarMaterial';
import { Documento } from '../../components/Documento';
import { TipoDocumento } from '../../utils/documento';

export interface AlunosType {
	nome: string;
	sobrenome: string;
	documento: string;
	tipoDocumento: TipoDocumento;
	email: string;
	id: string;
	senha: string;
	professorId: string;
	user: {
		ativo: true;
		avatarUrl: string;
	};
	userId: string;
}

const AlunoStarted: AlunosType = {
	nome: '',
	sobrenome: '',
	documento: '',
	tipoDocumento: 'CPF',
	email: '',
	id: '',
	senha: '',
	user: {
		ativo: true,
		avatarUrl: ''
	},
	userId: '',
	professorId: ''
};

export const Alunos = () => {
	const toast = useToast();
	const createDisclosure = useDisclosure();
	const updateDisclosure = useDisclosure();
	const materialDisclosure = useDisclosure();

	const {user, functionId} = useAuth();
	const [ alunos, setAlunos ] = useState<AlunosType[]>([]);
	const [ alunosMaterial, setAlunosMaterial ] = useState<AlunosType>(AlunoStarted);
	const [ alunoUpdate, setAlunoUpdate ] = useState<AlunosType>(AlunoStarted);
	const [ professores, setProfessores ] = useState<ProfessoresGet[]>([]);
	const [ professorId, setProfessorId ] = useState<string>('');
	const [ search, setSearch ] = useState<string>('');
	const [ loading, setLoading ] = useState<boolean>(true);
	const [ , setUpdateData ] = useState<boolean>(true);

	const buscarAlunos = useCallback((id: string) => {
		setLoading(true);
		listarAlunosPorProfessorId(id)
			.then((response) => {
				setLoading(false);
				setAlunos(response.data);
				setUpdateData(false);
			})
			.catch((error) => {
				if (!error.response) return;
				setLoading(false);
				toast({
					title: `Não foi possível listar alunos.`,
					status: 'error',
					isClosable: true
				});
			});
	}, [ toast ]);

	const buscarProfessores = useCallback(
		() => {
			setLoading(true);
			listarProfessores()
				.then((response) => {
					setLoading(false);
					setProfessores(response.data);
				})
				.catch((error) => {
					if (!error.response) return;
					setLoading(false);
					toast({
						title: `Não foi possível listar alunos.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(() => {
		if(user?.userType === "PROFESSOR"){
			buscarAlunos(functionId);
		}else{
			buscarProfessores();
		}
	}, [ buscarAlunos, buscarProfessores, functionId, user?.userType ]);

	const reload = () => {
		if (user?.userType === 'PROFESSOR') {
			buscarAlunos(functionId);
			return;
		}
		// Mesmo cuidado do filtro: sem professor escolhido nao ha o que recarregar.
		if (!professorId) {
			setAlunos([]);
			return;
		}
		buscarAlunos(professorId);
	};

	const deleteUser = (id: string, value: boolean) => {
		const result = value ? 'habilitado' : 'desabilitado';
		const action = value ? 'habilitar' : 'desabilitar';
		setLoading(true);
		disableUser(id, value)
			.then(() => {
				setUpdateData(true);
				setLoading(false);
				toast({
					title: `Aluno(a) ${result}`,
					status: 'success',
					isClosable: true
				});
			})
			.catch((error) => {
				if (!error.response) return;
				setLoading(false);
				toast({
					title: `Não foi possível ${action} aluno.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	// Admin so ve alunos depois de escolher um professor no filtro; ate la a lista
	// vazia nao significa "nenhum aluno cadastrado".
	const aguardandoProfessor = user?.userType === 'ADMIN' && !professorId;

	const handleSelectProfessor = (id: string) => {
		setProfessorId(id);
		// Voltar ao placeholder limpa a lista em vez de buscar por id vazio, que
		// nao casa com nenhuma rota do backend.
		if (!id) {
			setAlunos([]);
			return;
		}
		buscarAlunos(id);
	};

	return (
		<DashLayout>
			<Flex width="100%" height="100%" flexDir="column">
				<PageHeader title="Alunos">
					<Flex gap="20px" flexWrap="wrap">
						{ user?.userType === "ADMIN" &&
							<Select
								placeholder="Selecione o Professor"
								onChange={(e) => {
									handleSelectProfessor(e.target.value);
								}}
								value={professorId}
								borderColor="mist"
								bgColor="paper"
								width="35%"
							>
								{professores.map((item) => (
									<option
										value={item.id}
										key={item.id}
										style={{ color: 'black' }}
									>{`${item.nome} ${item.sobrenome}`}</option>
								))}
							</Select>
						}
						<InputGroup gap={'20px'}>
							<InputLeftElement pointerEvents="none" color={'gray.600'}>
								<FaSearch />
							</InputLeftElement>
							<Input
								type="tel"
								placeholder="Buscar alunos..."
								onChange={(e) => setSearch(e.target.value)}
								value={search}
								borderColor="mist"
								bgColor="paper"
							/>
							<Button
								leftIcon={<FaPlus />}
								colorScheme="blue"
								variant="solid"
								onClick={() => createDisclosure.onOpen()}
							>
								Novo
							</Button>
						</InputGroup>
					</Flex>
				</PageHeader>
				<Flex width="100%" bgColor="white" minH="100%">
					{!loading ? (
						alunos.length === 0 ? (
						aguardandoProfessor ? (
						<EmptyState
							icon={FaUserGraduate}
							title="Selecione um professor"
							description="A lista de alunos é filtrada por professor. Escolha um no campo acima para ver os alunos dele."
						/>
						) : (
						<EmptyState
							icon={FaUserGraduate}
							title="Nenhum aluno por aqui ainda"
							description="Cadastre o primeiro aluno para começar a acompanhar aulas, books e materiais."
							actionLabel="Novo aluno"
							onAction={() => createDisclosure.onOpen()}
						/>
						)
						) : (
						<Box overflowY="auto" overflowX="auto" width={{base:"90vw", md:"100vw", sm:"100vw"}} minH="100%" paddingBottom="200px">
							<Table variant="simple">
								<TableCaption>Alunos</TableCaption>
								<Thead position="sticky" top={0} bgColor="white" zIndex="999">
									<Tr>
										<Th>Nome</Th>
										<Th>Documento</Th>
										<Th>Email</Th>
										<Th>Status</Th>
										<Th>Ações</Th>
									</Tr>
								</Thead>
								<Tbody>
									{alunos && alunos.map((aluno, index) => 
										{
											if (
												!Object.values(aluno)
													.map((variavel) => variavel)
													.reduce((a, b) => (b = a + ' ' + b))
													.toLowerCase()
													.includes(search.toLowerCase())
											)
												return null;

											return (<Tr _hover={{ bgColor: 'gray.100', cursor: 'pointer' }} key={index}>
												<Td
													onClick={() => {
														setAlunoUpdate(aluno);
														updateDisclosure.onOpen();
													}}
												>
													<Flex gap={'10px'} alignItems={'center'}>
														<Avatar
															size="sm"
															name={aluno.nome + ' ' + aluno.sobrenome}
															src={aluno.user.avatarUrl}
														/>
														{aluno.nome + ' ' + aluno.sobrenome}
													</Flex>
												</Td>
												<Td
													onClick={() => {
														setAlunoUpdate(aluno);
														updateDisclosure.onOpen();
													}}
												>
													<Documento tipo={aluno.tipoDocumento} valor={aluno.documento} />
												</Td>
												<Td
													onClick={() => {
														setAlunoUpdate(aluno);
														updateDisclosure.onOpen();
													}}
												>
													{aluno.email}
												</Td>
												<Td>
													<Stack align="center" direction="row">
														<Switch
															size="lg"
															isChecked={aluno.user.ativo}
															onChange={() => deleteUser(aluno.userId, !aluno.user.ativo)}
														/>
													</Stack>
												</Td>
												<Td>
												<Button
													leftIcon={<BiBookBookmark />}
													colorScheme="blue"
													variant="solid"
													onClick={() => {
														setAlunosMaterial(aluno)
														setAlunoUpdate(aluno);
														materialDisclosure.onOpen()
													}}
												>
													Materiais
												</Button>
												</Td>
											</Tr>)
										}
									)}
								</Tbody>
							</Table>
						</Box>
						)
					) : (
						<Loading />
					)}
				</Flex>
			</Flex>
			<DrawerCreate
				isOpen={createDisclosure.isOpen}
				onOpen={createDisclosure.onOpen}
				onClose={createDisclosure.onClose}
				afterClose={reload}
				professores={professores}
			/>
			<DrawerUpdate
				isOpen={updateDisclosure.isOpen}
				onOpen={updateDisclosure.onOpen}
				onClose={updateDisclosure.onClose}
				afterClose={reload}
				aluno={alunoUpdate}
				professores={professores}
			/>
			<LiberarMaterial
				isOpen={materialDisclosure.isOpen}
				onOpen={materialDisclosure.onOpen}
				onClose={materialDisclosure.onClose}
				aluno={alunosMaterial}
				afterClose={reload}
			/>
		</DashLayout>
	);
};
